import {
  ForwardRefExoticComponent,
  PropsWithChildren,
  PropsWithoutRef,
  RefAttributes,
  SVGProps,
  useState,
} from 'react';
import BreadcrumbsTargetContext from '@/components/BreadcrumbsTargetContext';
import PageHeader from '@/components/PageHeader';
import { isPathActive } from '@/routing/isPathActive';
import { Link, useLocation } from 'react-router-dom';

export type Icon = ForwardRefExoticComponent<
  PropsWithoutRef<SVGProps<SVGSVGElement>> & {
    title?: string;
    titleId?: string;
  } & RefAttributes<SVGSVGElement>
>;

export interface Section {
  href: string;
  title: string;
  icon: Icon;
  children?: Section[];
}

interface SettingsProps extends PropsWithChildren {
  sections: Section[];
}

export const Settings = ({ children, sections }: SettingsProps) => {
  const [breadcrumbsTarget, setBreadcrumbsTarget] = useState<HTMLDivElement | null>(null);
  const location = useLocation();

  const childLinks = sections.flatMap((section) =>
    isPathActive(section, location) && section.children ? section.children : [],
  );

  const hasChildLinks = !!childLinks.length;

  return (
    <BreadcrumbsTargetContext.Provider value={breadcrumbsTarget}>
      <div ref={setBreadcrumbsTarget} className="settings-print-hidden" />
      <div className="settings-print-hidden">
        <PageHeader title="Settings" height={10} />
      </div>

      <div className="settings-layout flex flex-col md:flex-row mt-4">
        <nav className="settings-print-hidden w-full md:w-64 flex-shrink-0 mb-4 md:mb-0 md:mr-8">
          <ul className="flex md:flex-col flex-wrap md:flex-nowrap gap-2">
            {sections.map((section) => (
              <li key={section.title}>
                <Link
                  to={section.href}
                  className={`flex p-3 py-2 rounded-lg text-sm gap-4 ${
                    isPathActive(section, location)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-black hover:bg-gray-300'
                  }`}
                >
                  <section.icon className="w-6 h-6" />
                  {section.title}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex-grow">
          <div className="settings-print-content bg-white mx-auto rounded-lg shadow p-6 lg:p-12">
            {hasChildLinks && (
              <ul className="flex flex-wrap text-sm font-medium text-center text-gray-500 dark:text-gray-400 mb-8">
                {sections.map((section) =>
                  isPathActive(section, location) && section.children
                    ? section.children.map((child) => (
                        <li key={child.href} className="me-2">
                          <Link
                            to={child.href}
                            className={`inline-flex items-center px-2 py-2 rounded-lg ${
                              isPathActive(child, location)
                                ? 'text-white bg-blue-600'
                                : 'hover:text-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-white'
                            }`}
                            aria-current={isPathActive(child, location) ? 'page' : undefined}
                          >
                            <child.icon className="w-6 h-6 mr-2" />
                            {child.title}
                          </Link>
                        </li>
                      ))
                    : null,
                )}
              </ul>
            )}

            <div>{children}</div>
          </div>
        </div>
      </div>
    </BreadcrumbsTargetContext.Provider>
  );
};

export default Settings;
